/**
 * Profile, workspace and account settings DTOs (REST /v1).
 *
 * - **Profile** (per workspace, like Slack): names, title, pronouns, phone (private unless shared), timezone,
 *   language (also the person's language for emails/pushes), status, and a photo. Photos and workspace icons are
 *   files (files.ts) uploaded with `purpose: "avatar"` / `"workspace_icon"` (512×512 WebP made on the client), then
 *   attached by `UpdateProfileRequest.avatarFileId` / `UpdateWorkspaceRequest.iconFileId`; every member can view them.
 * - **Members and roles**: owners and admins manage people; the permission matrix is core `logic/roles` (the same
 *   rules on the server and the clients). Role and activation changes emit `member.updated`; workspace changes emit
 *   `workspace.updated` (realtime.ts).
 * - **Account** (not workspace-scoped): sign-in methods, sessions, and in-app account deletion (App Store 5.1.1(v),
 *   Google Play): memberships are deactivated, every profile anonymized, sessions and push devices revoked. A primary
 *   or last owner must transfer ownership first.
 */
import { z } from "zod";
import { Invite } from "./api.ts";
import { AccessPolicy, DndSchedule, Id, Locale, Member, MemberAvatar, Role, Timestamp, UserStatus, Workspace, WorkspaceFeatures, FeatureAdminAccess, HuddleLinkSettings } from "./entities.ts";

export const DISPLAY_NAME_MAX = 80;
export const FULL_NAME_MAX = 80;
export const TITLE_MAX = 100;
export const PRONOUNS_MAX = 30;
export const PHONE_MAX = 32;
export const STATUS_TEXT_MAX = 100;
export const STATUS_EMOJI_MAX = 32;
export const TIMEZONE_MAX = 64;
export const WORKSPACE_NAME_MAX = 80;

// ---------- Profile ----------

/**
 * My profile in one workspace. Absent fields stay as they are; an empty `title`, `pronouns`, `phone` or `timezone`
 * clears it; an empty `fullName` falls back to the display name; a status with empty `emoji` and `text` clears it.
 * `avatarFileId`: a ready upload of mine made with `purpose: "avatar"`; `removeAvatar` goes back to initials.
 */
export const UpdateProfileRequest = z.object({
  displayName: z.string().min(1).max(DISPLAY_NAME_MAX).optional(),
  fullName: z.string().max(FULL_NAME_MAX).optional(),
  title: z.string().max(TITLE_MAX).optional(),
  pronouns: z.string().max(PRONOUNS_MAX).optional(),
  phone: z.string().max(PHONE_MAX).optional(),
  phoneVisible: z.boolean().optional(),
  /** IANA zone ("America/Sao_Paulo"). */
  timezone: z.string().max(TIMEZONE_MAX).optional(),
  /** App language: also used for my emails and push notifications. */
  locale: Locale.optional(),
  status: UserStatus.optional(),
  avatarFileId: Id.optional(),
  removeAvatar: z.boolean().optional(),
  /** A non-photo avatar: `initials` or `emoji` on a tone (a `photo` is set with `avatarFileId`). */
  avatar: MemberAvatar.optional(),
  /** New username (core `logic/handles` rules; 409 `handle_taken`). */
  handle: z.string().min(1).max(40).optional(),
});
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequest>;

export const HANDLE_MIN = 2;
export const HANDLE_MAX = 32;

/** Why a username can't be used (core `logic/handles#handleProblem`; `taken` comes from the server). */
export const HandleProblem = z.enum(["tooShort", "tooLong", "characters", "edges", "reserved", "taken"]);
export type HandleProblem = z.infer<typeof HandleProblem>;

/** Live username check while typing: the normalized handle, whether it's free for me, and free alternatives. */
export const HandleAvailabilityResponse = z.object({
  handle: z.string(),
  available: z.boolean(),
  problem: HandleProblem.optional(),
  suggestions: z.array(z.string()),
});
export type HandleAvailabilityResponse = z.infer<typeof HandleAvailabilityResponse>;

// ---------- Workspace ----------

/**
 * Admins and owners. `slug` must be free and valid (core `logic/slug`): old links stop working after a change.
 * `iconFileId`: a ready upload of mine made with `purpose: "workspace_icon"`.
 */
export const UpdateWorkspaceRequest = z.object({
  name: z.string().min(1).max(WORKSPACE_NAME_MAX).optional(),
  slug: z.string().min(1).max(32).optional(),
  iconFileId: Id.optional(),
  removeIcon: z.boolean().optional(),
  defaultLocale: Locale.optional(),
  /** AI thread summaries on/off (off also deletes the summaries already written). */
  aiThreadSummaries: z.boolean().optional(),
  /** Link previews on/off (off hides the previews already shown; new links aren't fetched). */
  unfurlLinks: z.boolean().optional(),
  /** "Sent with Bundle" on members' emails on/off (off leaves it out of every message sent from then on). */
  mailSentWith: z.boolean().optional(),
  /**
   * Product areas on/off, merged into the current ones (core `logic/features#applyFeatures`). Off hides the area and
   * blocks its routes (403 `feature_disabled`); nothing is deleted. 400 `feature_last_enabled` when every area would
   * be off.
   */
  features: WorkspaceFeatures.optional(),
  /** What owners and admins keep of switched-off areas (`none` ⇒ hidden for them too). */
  featureAdminAccess: FeatureAdminAccess.optional(),
  /** Settings → Huddles, merged into the current ones. */
  huddleLinks: HuddleLinkSettings.partial().optional(),
});
export type UpdateWorkspaceRequest = z.infer<typeof UpdateWorkspaceRequest>;

export const WorkspaceResponse = z.object({ workspace: Workspace });
export type WorkspaceResponse = z.infer<typeof WorkspaceResponse>;

/** Hands primary ownership to an active, unrestricted person (they become an owner). Primary owner only. */
export const TransferOwnershipRequest = z.object({ userId: Id });
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequest>;

// ---------- Join by email domain ----------

/** Domains one workspace may allow (core `logic/emailDomains#MAX_EMAIL_DOMAINS`). */
export const EMAIL_DOMAINS_MAX = 10;

/**
 * Allowed email domains: anyone signed in with a verified address on one of them can join as a member without an
 * invite. Exact domains, lower case (core `logic/emailDomains`); public mail providers are refused.
 */
export const EmailDomainsResponse = z.object({ domains: z.array(z.string()) });
export type EmailDomainsResponse = z.infer<typeof EmailDomainsResponse>;

/**
 * Replaces the whole list (empty turns joining by domain off). Admins and owners. 400 `email_domain_invalid` /
 * `email_domain_public` / `email_domain_limit` (the message names the first offending entry).
 */
export const UpdateEmailDomainsRequest = z.object({ domains: z.array(z.string().max(300)).max(50) });
export type UpdateEmailDomainsRequest = z.infer<typeof UpdateEmailDomainsRequest>;

/** A workspace I can join because it allows my verified email's domain. */
export const JoinableWorkspace = z.object({
  id: Id,
  slug: z.string(),
  name: z.string(),
  iconUrl: z.string().optional(),
  /** The allowed domain that matched (mine). */
  domain: z.string(),
  /** Active people in it. */
  memberCount: z.number().int(),
});
export type JoinableWorkspace = z.infer<typeof JoinableWorkspace>;

/** Empty when my email is unverified, an Apple relay address, or on a public mail provider. */
export const JoinableWorkspacesResponse = z.object({ workspaces: z.array(JoinableWorkspace) });
export type JoinableWorkspacesResponse = z.infer<typeof JoinableWorkspacesResponse>;

/** Joined as a member (default channels included); `alreadyMember` when I was one already. */
export const JoinWorkspaceResponse = z.object({ workspace: Workspace, alreadyMember: z.boolean() });
export type JoinWorkspaceResponse = z.infer<typeof JoinWorkspaceResponse>;

// ---------- Activity ----------

/**
 * One item in my Activity feed (core `logic/activity` renders it). Everyone's own items:
 * - `mention`: someone @-mentioned me by name, or @here/@channel/@everyone (`broadcast`) in a channel (DMs have their
 *   own unread state and stay out of Activity). `preview`: the message as one line of plain text.
 * - `thread_reply`: new replies in a thread I follow (I started it or replied in it), one item per thread: `userIds`
 *   = who replied (newest first), `count` = replies since I last read the item, `messageId` = the newest reply.
 * - `reaction`: reactions to one of my messages, one item per message and emoji ("Bo and 2 others reacted 👍"):
 *   `userIds` = who reacted (newest first); `preview` = my message.
 * - `channel_added`: someone added me to a channel.
 * - `reminder`: one of my Later reminders came due (later.ts): `laterId` = the Later item; a saved message's
 *   `channelId` / `messageId` / `threadRootId`, or none for a text reminder; `preview` = the message or the text.
 * - `event_reminder`: a calendar event of mine starts soon (calendar.ts `reminderMinutes`): `eventId`, `at` = the
 *   occurrence's original start (its id in a series), `start` = when it starts, `title`, `allDay`. One item per
 *   occurrence. An invitation, a change of time or a cancellation by the organizer is the same kind with `change` and
 *   `by` (who did it), one item per event, so older apps list it as an upcoming event instead of failing on a new kind.
 * And `member_joined`: someone joined the workspace through an allowed email domain (`via: "domain"`, the domain that
 * matched) — owners and admins.
 * `unread`: true until I open it or mark everything read (absent = read; old servers never send it).
 */
export const ActivityItem = z.discriminatedUnion("kind", [
  z.object({ id: Id, kind: z.literal("member_joined"), userId: Id, via: z.literal("domain"), domain: z.string(), ts: Timestamp, unread: z.boolean().optional() }),
  z.object({
    id: Id,
    kind: z.literal("mention"),
    /** Who mentioned me. */
    userId: Id,
    channelId: Id,
    messageId: Id,
    threadRootId: Id.optional(),
    broadcast: z.enum(["here", "channel", "everyone"]).optional(),
    /** Reached through a user group (userGroups.ts): its handle when the message was sent, without the `@`. */
    groupHandle: z.string().optional(),
    preview: z.string(),
    ts: Timestamp,
    unread: z.boolean().optional(),
  }),
  z.object({
    id: Id,
    kind: z.literal("thread_reply"),
    userIds: z.array(Id),
    count: z.number().int(),
    channelId: Id,
    threadRootId: Id,
    messageId: Id,
    preview: z.string(),
    ts: Timestamp,
    unread: z.boolean().optional(),
  }),
  z.object({
    id: Id,
    kind: z.literal("reaction"),
    userIds: z.array(Id),
    emoji: z.string(),
    channelId: Id,
    messageId: Id,
    threadRootId: Id.optional(),
    preview: z.string(),
    ts: Timestamp,
    unread: z.boolean().optional(),
  }),
  z.object({ id: Id, kind: z.literal("channel_added"), userId: Id, channelId: Id, ts: Timestamp, unread: z.boolean().optional() }),
  z.object({
    id: Id,
    kind: z.literal("reminder"),
    laterId: Id,
    channelId: Id.optional(),
    messageId: Id.optional(),
    threadRootId: Id.optional(),
    preview: z.string(),
    ts: Timestamp,
    unread: z.boolean().optional(),
  }),
  z.object({
    id: Id,
    kind: z.literal("event_reminder"),
    eventId: Id,
    at: Timestamp,
    start: Timestamp,
    title: z.string(),
    allDay: z.boolean().optional(),
    change: z.enum(["invited", "changed", "cancelled"]).optional(),
    by: Id.optional(),
    ts: Timestamp,
    unread: z.boolean().optional(),
  }),
]);
export type ActivityItem = z.infer<typeof ActivityItem>;

/**
 * GET /v1/w/:ws/activity — newest first (at most 100). `?all=1` asks for every kind; without it the server lists
 * only `member_joined` (what clients before mentions/threads/reactions understand). `unread`: my unread items.
 */
export const ListActivityResponse = z.object({ items: z.array(ActivityItem), unread: z.number().int().optional() });
export type ListActivityResponse = z.infer<typeof ListActivityResponse>;

/** Marks these items read, or every item (`all`). → my unread count after it. */
export const MarkActivityReadRequest = z.object({ ids: z.array(Id).max(200).optional(), all: z.boolean().optional() });
export type MarkActivityReadRequest = z.infer<typeof MarkActivityReadRequest>;
export const MarkActivityReadResponse = z.object({ unread: z.number().int() });
export type MarkActivityReadResponse = z.infer<typeof MarkActivityReadResponse>;

// ---------- Notifications (Do not disturb) ----------

// DndSchedule (the daily quiet-hours window) lives in entities.ts: members carry it too (Member.dnd).

/**
 * My notification settings (per person, every workspace and device). While paused (`pausedUntil` in the future, or
 * inside the schedule) the API sends no pushes and clients skip sounds and banners; Activity still fills up.
 */
export const NotificationSettings = z.object({
  pausedUntil: Timestamp.optional(),
  schedule: DndSchedule.optional(),
});
export type NotificationSettings = z.infer<typeof NotificationSettings>;

/** GET/PUT /v1/account/notifications (PUT replaces my settings: an absent field clears it). */
export const NotificationSettingsResponse = z.object({ settings: NotificationSettings });
export type NotificationSettingsResponse = z.infer<typeof NotificationSettingsResponse>;

// ---------- Notification kinds ----------

/**
 * What can notify me (Settings → Notifications → "Notify me about"): `dm` and `group` a direct or group message,
 * `mention` my name or a group I'm in, `broadcast` @channel/@here/@everyone, `thread` a reply in a thread I follow,
 * `channel` any other message in a channel I joined, `huddle` a ring, `eventReminder` and `eventInvite` a calendar
 * reminder and an invitation, a change or a cancellation, `later` a Later reminder, `memberJoined` someone joined by
 * email domain (owners and admins). Mail has its own preferences (MailNotifyPrefs).
 */
export const NotifyKind = z.enum(["dm", "group", "mention", "broadcast", "thread", "channel", "huddle", "eventReminder", "eventInvite", "later", "memberJoined"]);
export type NotifyKind = z.infer<typeof NotifyKind>;

/**
 * My switches in one workspace, on every device: the kinds I turned off (empty, the default: everything on). The server
 * applies them (core logic/notifyKinds `wantsAlert`), so every client version follows them. They stop pushes, banners and
 * sounds; Activity and unread counts stay.
 */
export const NotifyKindPrefs = z.object({ off: z.array(NotifyKind).max(11) });
export type NotifyKindPrefs = z.infer<typeof NotifyKindPrefs>;

/** GET/PUT /v1/w/:ws/notifications/kinds (PUT replaces my switches). */
export const NotifyKindPrefsResponse = z.object({ prefs: NotifyKindPrefs });
export type NotifyKindPrefsResponse = z.infer<typeof NotifyKindPrefsResponse>;

// ---------- Navigation (phone tabs) ----------

/**
 * An area a phone's bottom bar can show between Home and Activity. The app's default order and tab count live in
 * packages/core/config/navigation.json (core logic/navigation). `files` (Files, docs/features/files.md) has no
 * workspace switch: it's always on.
 */
export const PhoneArea = z.enum(["mail", "calendar", "contacts", "huddles", "docs", "files"]);
export type PhoneArea = z.infer<typeof PhoneArea>;

/**
 * My phone tabs (per person, every workspace and device; Settings → Navigation). `phoneOrder`: areas in my order (the
 * app's order follows for any I haven't placed); `phoneHidden`: areas I took off the bar (Home's top row shows them).
 * Areas a workspace has off never show. Absent fields = the app's defaults.
 */
export const NavigationSettings = z.object({
  phoneOrder: z.array(PhoneArea).max(16).optional(),
  phoneHidden: z.array(PhoneArea).max(16).optional(),
});
export type NavigationSettings = z.infer<typeof NavigationSettings>;

// ---------- Appearance (accent colour) ----------

/** The accent colour sets (packages/tokens tokens.json `accent`; indigo is the default). */
export const AccentColor = z.enum(["indigo", "teal", "orange", "purple"]);
export type AccentColor = z.infer<typeof AccentColor>;

/** Light, dark, or follow the device. */
export const ThemeSetting = z.enum(["system", "light", "dark"]);
export type ThemeSetting = z.infer<typeof ThemeSetting>;

/**
 * My look on every device (Settings → Appearance, like Slack's themes). `accent` absent = the default (indigo);
 * `theme` absent = follow the device ("system").
 */
export const AppearanceSettings = z.object({ accent: AccentColor.optional(), theme: ThemeSetting.optional() });
export type AppearanceSettings = z.infer<typeof AppearanceSettings>;

/** GET/PUT /v1/account/appearance (PUT replaces my settings: an absent field clears it). */
export const AppearanceSettingsResponse = z.object({ settings: AppearanceSettings });
export type AppearanceSettingsResponse = z.infer<typeof AppearanceSettingsResponse>;

/** GET/PUT /v1/account/navigation (PUT replaces my settings: an absent field clears it). */
export const NavigationSettingsResponse = z.object({ settings: NavigationSettings });
export type NavigationSettingsResponse = z.infer<typeof NavigationSettingsResponse>;

// ---------- Members & roles ----------

/** The people directory for settings: everyone the caller may see, deactivated members included. */
export const ListMembersResponse = z.object({ members: z.array(Member) });
export type ListMembersResponse = z.infer<typeof ListMembersResponse>;

/**
 * Owner/admin/member/guest (core `logic/roles#roleChangeProblem`). Guest → member/admin/owner drops the access
 * restriction; member/admin → guest restricts them with `access` (the scope editor), or the guest defaults with the
 * caller as sponsor. Agents keep their role (edit their scope with updateAccess).
 */
export const ChangeRoleRequest = z.object({ role: Role, access: AccessPolicy.optional() });
export type ChangeRoleRequest = z.infer<typeof ChangeRoleRequest>;

/** An invite that can still be used (admins see all; others see their own). */
export const PendingInvite = z.object({
  id: Id,
  role: Role,
  /** Email invites (single use); absent for shareable links. */
  email: z.string().optional(),
  createdBy: Id,
  createdAt: Timestamp,
  expiresAt: Timestamp,
  /** Times a link was used. */
  useCount: z.number().int(),
});
export type PendingInvite = z.infer<typeof PendingInvite>;

export const ListInvitesResponse = z.object({ invites: z.array(PendingInvite) });
export type ListInvitesResponse = z.infer<typeof ListInvitesResponse>;

/** "Resend": a new token (the old one stops working), emailed again when email is configured. */
export const InviteResponse = z.object({ invite: Invite });
export type InviteResponse = z.infer<typeof InviteResponse>;

// ---------- Account ----------

export const SignInProvider = z.enum(["email", "google", "apple"]);
export type SignInProvider = z.infer<typeof SignInProvider>;

export const OwnedWorkspace = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  /** I'm its primary owner. */
  primary: z.boolean(),
  /** Other active owners. */
  otherOwners: z.number().int(),
});
export type OwnedWorkspace = z.infer<typeof OwnedWorkspace>;

// ---------- Account & sign-in (docs/features/account-sign-in.md) ----------

/**
 * An address that signs in to my account by email code (and password). The primary one gets mail from Bundle;
 * secondary ones only sign in. Unverified ones are waiting for their 6-digit code.
 */
export const AccountEmail = z.object({
  /** `primary` for the account's address; otherwise the secondary address's id. */
  id: z.string(),
  email: z.string(),
  primary: z.boolean(),
  verified: z.boolean(),
  addedAt: Timestamp,
});
export type AccountEmail = z.infer<typeof AccountEmail>;

/** A passkey (WebAuthn credential) that signs in to my account. Rename/delete: POST /auth/passkey/update-passkey, delete-passkey. */
export const AccountPasskey = z.object({
  id: Id,
  /** What I called it ("MacBook Touch ID"); absent when never named. */
  name: z.string().optional(),
  createdAt: Timestamp,
  /** Last sign-in with it. */
  lastUsedAt: Timestamp.optional(),
  /** `multiDevice`: synced (iCloud Keychain, Google Password Manager); `singleDevice`: a security key or one device. */
  deviceType: z.enum(["singleDevice", "multiDevice"]),
  /** Backed up by its provider (synced passkeys). */
  backedUp: z.boolean(),
});
export type AccountPasskey = z.infer<typeof AccountPasskey>;

/** A verified phone number on my account (sign-in by text, two-step backup). */
export const AccountPhone = z.object({
  /** Masked with its country code: "+1 ••• ••• 4321". */
  number: z.string(),
  /** The last four digits ("4321"), for "Text a code to •• ••• 4321". */
  last4: z.string(),
  verifiedAt: Timestamp,
});
export type AccountPhone = z.infer<typeof AccountPhone>;

export const PhoneImportSource = z.enum(["google", "slack"]);
export type PhoneImportSource = z.infer<typeof PhoneImportSource>;

/** A number read from Google or Slack, offered to pre-fill the phone step. My own number, so not masked. */
export const PhoneImport = z.object({
  /** E.164 ("+14155554321"). */
  e164: z.string(),
  /** ISO 3166-1 alpha-2 of the number. */
  country: z.string().length(2),
  /** The national part, formatted for that country ("(415) 555-4321"). */
  national: z.string(),
  source: PhoneImportSource,
});
export type PhoneImport = z.infer<typeof PhoneImport>;

/** How a code reaches the phone: a text, or a call that reads it out ("Call me"). */
export const PhoneChannel = z.enum(["sms", "voice"]);
export type PhoneChannel = z.infer<typeof PhoneChannel>;

export const AccountResponse = z.object({
  userId: Id,
  email: z.string().optional(),
  name: z.string(),
  locale: Locale,
  /** Sign-in methods linked to the account (email codes always work for the account's address). */
  providers: z.array(SignInProvider),
  /** Workspaces I own. */
  ownedWorkspaces: z.array(OwnedWorkspace),
  /** Workspaces whose ownership I must transfer before I can delete my account (primary or last owner). */
  deletionBlockedBy: z.array(Id),
  /** Primary address first, then secondary ones (oldest first). Absent from older servers. */
  emails: z.array(AccountEmail).optional(),
  /** A password is set (sign in with email + password). */
  hasPassword: z.boolean().optional(),
  /**
   * This session just created the account (Create account, within 10 minutes): a first password needs no emailed code
   * (stepUpFor). Absent or false everywhere else.
   */
  freshSignup: z.boolean().optional(),
  /** Passkeys, newest first. */
  passkeys: z.array(AccountPasskey).optional(),
  /** Two-step verification (authenticator app) is on: sign-ins other than passkeys ask for a code. */
  twoFactorEnabled: z.boolean().optional(),
  /** Unused backup codes (of 10, each works once; 0 when two-step verification is off). */
  backupCodesRemaining: z.number().int().nonnegative().optional(),
  /** Email codes work for this account (the deployment sends email and the primary address can receive it). */
  emailCode: z.boolean().optional(),
  /** Phone number sign-in is available here (a text sender is set up). Absent or false: no phone UI anywhere. */
  phoneSignIn: z.boolean().optional(),
  /** My verified phone number, if any. */
  phone: AccountPhone.optional(),
  /** A number from Google or Slack that pre-fills "Verify your phone" (not verified, never trusted until it is). */
  phoneImport: PhoneImport.optional(),
  /** My email is hosted on Bundle: a phone number is required (no "Skip for now", can't be removed, only changed). */
  phoneRequired: z.boolean().optional(),
  /** Countries we can text (ISO 3166-1 alpha-2), for the country picker. */
  phoneCountries: z.array(z.string().length(2)).optional(),
  /** "Call me" works here (a voice-capable number is set up). Absent or false: offer texts only. */
  phoneVoice: z.boolean().optional(),
});
export type AccountResponse = z.infer<typeof AccountResponse>;

/**
 * POST /v1/account/phone/code: texts (or calls with) a 6-digit code to a number I want to verify. Adding or changing a
 * number proves it's me first (`currentPassword`, or `emailCode` from POST /v1/account/step-up-code), except on the
 * session that just created the account. Errors: 400 `invalid_phone` / `country_not_supported` / `us_not_ready` / `ca_not_ready` (US
 * and Canadian texts until our number there is approved) / `voice_unavailable`, 409 `phone_in_use`,
 * 403 `password_required` / `email_code_required`, 400 `invalid_password` / `invalid_otp`, 429 `resend_wait` (with
 * `retryIn`) / `rate_limited`, 502 `sms_not_sent`, 503 `phone_unavailable`.
 */
export const SendPhoneCodeRequest = z.object({
  /** ISO 3166-1 alpha-2 of the number (the country picker). Ignored when `number` starts with "+". */
  country: z.string().length(2),
  number: z.string().min(3).max(32),
  channel: PhoneChannel.optional(),
  currentPassword: z.string().max(1024).optional(),
  emailCode: z.string().max(12).optional(),
});
export type SendPhoneCodeRequest = z.infer<typeof SendPhoneCodeRequest>;

export const SendPhoneCodeResponse = z.object({
  /** Where the code went, masked with its country code ("+1 ••• ••• 4321"). */
  phone: z.string(),
  /** Seconds before another code can be asked for. */
  resendIn: z.number().int(),
  /** Seconds the code works for. */
  expiresIn: z.number().int(),
});
export type SendPhoneCodeResponse = z.infer<typeof SendPhoneCodeResponse>;

/** PUT /v1/account/phone: the code from the text makes that number mine (400 `invalid_code`, 429 `too_many_attempts`). */
export const VerifyPhoneRequest = z.object({ code: z.string().min(1).max(12) });
export type VerifyPhoneRequest = z.infer<typeof VerifyPhoneRequest>;

export const PhoneResponse = z.object({ phone: AccountPhone });
export type PhoneResponse = z.infer<typeof PhoneResponse>;

/** DELETE /v1/account/phone: proof as for adding (409 `phone_required` when my email is hosted on Bundle). */
export const RemovePhoneRequest = z.object({ currentPassword: z.string().max(1024).optional(), emailCode: z.string().max(12).optional() });
export type RemovePhoneRequest = z.infer<typeof RemovePhoneRequest>;

/**
 * POST /v1/account/phone/import/google: reads my number from Google (People API, scope
 * https://www.googleapis.com/auth/user.phonenumbers.read, asked for only at the phone step). Native apps send the
 * access token their Google SDK granted; the web links Google with that scope first and sends nothing (the server
 * uses the stored token). 409 `google_scope_required` when no token has the scope.
 */
export const ImportGooglePhoneRequest = z.object({ accessToken: z.string().max(4096).optional() });
export type ImportGooglePhoneRequest = z.infer<typeof ImportGooglePhoneRequest>;

export const ImportGooglePhoneResponse = z.object({ phoneImport: PhoneImport.nullable() });
export type ImportGooglePhoneResponse = z.infer<typeof ImportGooglePhoneResponse>;

export const ListAccountEmailsResponse = z.object({ emails: z.array(AccountEmail) });
export type ListAccountEmailsResponse = z.infer<typeof ListAccountEmailsResponse>;

/** POST /v1/account/emails: emails a 6-digit code to the new address (again: resends it). */
export const AddAccountEmailRequest = z.object({ email: z.string().min(3).max(254) });
export type AddAccountEmailRequest = z.infer<typeof AddAccountEmailRequest>;

export const VerifyAccountEmailRequest = z.object({ code: z.string().min(1).max(12) });
export type VerifyAccountEmailRequest = z.infer<typeof VerifyAccountEmailRequest>;

export const AccountEmailResponse = z.object({ email: AccountEmail });
export type AccountEmailResponse = z.infer<typeof AccountEmailResponse>;

/** POST /v1/account/email-change/undo (no auth): the token from the link emailed to the old address. */
export const UndoEmailChangeRequest = z.object({ token: z.string().min(16).max(200) });
export type UndoEmailChangeRequest = z.infer<typeof UndoEmailChangeRequest>;

/** The primary address is the old one again; every session was signed out. */
export const UndoEmailChangeResponse = z.object({ email: z.string() });
export type UndoEmailChangeResponse = z.infer<typeof UndoEmailChangeResponse>;

/**
 * PUT /v1/account/password: set or change my password. With a password already set, `currentPassword` proves it's
 * me; without one, `emailCode` (POST /v1/account/step-up-code) does. Other sessions are signed out unless
 * `signOutOthers` is false.
 */
export const SetPasswordRequest = z.object({
  newPassword: z.string().min(1).max(1024),
  currentPassword: z.string().max(1024).optional(),
  emailCode: z.string().max(12).optional(),
  signOutOthers: z.boolean().optional(),
});
export type SetPasswordRequest = z.infer<typeof SetPasswordRequest>;

/** DELETE /v1/account/password (refused with `last_sign_in_method` when nothing else signs in). */
export const RemovePasswordRequest = z.object({ currentPassword: z.string().max(1024) });
export type RemovePasswordRequest = z.infer<typeof RemovePasswordRequest>;

export const PasswordResponse = z.object({ hasPassword: z.boolean(), signedOut: z.number().int().nonnegative() });
export type PasswordResponse = z.infer<typeof PasswordResponse>;

/** POST /v1/account/step-up-code: a 6-digit code to my primary address, for changes that need proof it's me. */
export const StepUpCodeResponse = z.object({ email: z.string(), expiresIn: z.number().int() });
export type StepUpCodeResponse = z.infer<typeof StepUpCodeResponse>;

/** A signed-in browser or app. */
export const AccountSession = z.object({
  id: Id,
  createdAt: Timestamp,
  /** Last refreshed. */
  updatedAt: Timestamp,
  expiresAt: Timestamp,
  userAgent: z.string().optional(),
  /** Desktop app sessions: "Bundle for Mac — Ana's MacBook Pro". */
  deviceName: z.string().optional(),
  /** Where it was signed in or last refreshed (Cloudflare's estimate from the IP address). */
  city: z.string().optional(),
  /** ISO 3166-1 alpha-2. */
  country: z.string().length(2).optional(),
  /** The session behind this request. */
  current: z.boolean(),
});
export type AccountSession = z.infer<typeof AccountSession>;

export const ListSessionsResponse = z.object({ sessions: z.array(AccountSession) });
export type ListSessionsResponse = z.infer<typeof ListSessionsResponse>;

export const RevokeSessionsResponse = z.object({ revoked: z.number().int() });
export type RevokeSessionsResponse = z.infer<typeof RevokeSessionsResponse>;

// ---------- Data export (Settings → Data; LGPD art. 18 / GDPR art. 15 and 20) ----------

/**
 * `account`: my profile, my messages, my files list and the pages I created, in every workspace I belong to.
 * `workspace` (owners and admins): the workspace's members, public channels with their messages, and its shared
 * (workspace-visible) pages. Private channels, DMs and private pages are never in a workspace export.
 */
export const DataExportScope = z.enum(["account", "workspace"]);
export type DataExportScope = z.infer<typeof DataExportScope>;

/** A finished export: a .zip of JSON + Markdown kept for a day. `url` needs no bearer token and expires first. */
export const DataExport = z.object({
  id: Id,
  scope: DataExportScope,
  /** `workspace` exports only. */
  workspaceId: Id.optional(),
  createdAt: Timestamp,
  /** Bytes. */
  size: z.number().int().nonnegative(),
  /** Short-lived download link (`GET /v1/exports/:id?t=…`). */
  url: z.string(),
  urlExpiresAt: Timestamp,
});
export type DataExport = z.infer<typeof DataExport>;

/**
 * GET: my latest export of that scope (absent when none is kept). POST: a new one, built right away. One export per
 * scope every 10 minutes: POST answers 429 `rate_limited` until `nextAllowedAt`.
 */
export const DataExportResponse = z.object({
  export: DataExport.optional(),
  /** When the next export may be requested (absent: now). */
  nextAllowedAt: Timestamp.optional(),
});
export type DataExportResponse = z.infer<typeof DataExportResponse>;

/** Typed confirmation: the account's email address (case-insensitive). People only (agent tokens can't). */
export const DeleteAccountRequest = z.object({ confirmEmail: z.string().min(3).max(320) });
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>;

/** The account is gone: every session is revoked, so the client signs out locally. */
export const DeleteAccountResponse = z.object({ deleted: z.boolean(), workspaces: z.number().int() });
export type DeleteAccountResponse = z.infer<typeof DeleteAccountResponse>;
